import {
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
  inject,
  input,
  output,
  WritableSignal,
  signal,
} from '@angular/core';
import { PlatformIdService, PROVIDER_NAMES } from '@ckmk/angular';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NavigationStart, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';

@Component({
  selector: 'go-top',
  standalone: true,
  imports: [FaIconComponent],
  templateUrl: './go-top.html',
  styleUrl: './go-top.scss',
  host: {
    '[class.visible]': 'visible()',
    '(click)': 'onClick($event)',
    '(window:scroll)': 'onDocumentScroll($event)',
  },
  encapsulation: ViewEncapsulation.None,
})
export class GoTop implements OnInit, OnChanges, OnDestroy {
  protected readonly self = inject(ElementRef);

  protected readonly router = inject(Router);

  protected readonly platform = inject(PlatformIdService);

  protected readonly icons = inject(PROVIDER_NAMES.ICONS);

  public readonly currentTop = input<number>();

  public readonly clickEmitter = output<PointerEvent>({ alias: 'onClick' });

  protected readonly visible: WritableSignal<boolean> = signal<boolean>(false);

  protected readonly localCurrentTop: WritableSignal<number> =
    signal<number>(0);

  protected readonly subscriptions: WritableSignal<Subscription[]> = signal<
    Subscription[]
  >([]);

  public constructor() {}

  public ngOnInit() {
    if (this.platform.isBrowser()) {
      this.subscriptions.set([
        this.router.events
          .pipe(filter((event) => event instanceof NavigationStart))
          .subscribe({
            next: (event) => {
              this.scrollTopTop('instant');
            },
          }),
      ]);
    }
  }

  public ngOnChanges({ currentTop }: SimpleChanges) {
    if (currentTop && !currentTop.firstChange) {
      this.onCurrentTop(currentTop.currentValue, currentTop.previousValue);
    }
  }

  public ngOnDestroy() {
    this.subscriptions.update((list) =>
      list.filter((sub) => sub.unsubscribe()),
    );
  }

  protected onCurrentTop(newValue?: number, oldValue?: number): void {
    if (typeof newValue === 'number' && typeof oldValue === 'number') {
      if (newValue < 250) {
        if (this.visible()) {
          this.visible.set(false);
        }
      } else if (newValue > 300) {
        if (newValue > oldValue) {
          if (this.visible()) {
            this.visible.set(false);
          }
        } else {
          if (!this.visible()) {
            this.visible.set(true);
          }
        }
      }

      this.localCurrentTop.set(newValue);
    }
  }

  protected scrollTopTop(behavior: ScrollBehavior = 'smooth'): void {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior,
    });
  }

  protected onClick(event: PointerEvent): void {
    if (typeof this.currentTop() === 'number') {
      this.clickEmitter.emit(event);
    } else {
      this.scrollTopTop();
    }
  }

  protected onDocumentScroll(event: Event): void {
    this.onCurrentTop(window.scrollY, this.localCurrentTop());
  }
}
